import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { UserDetailModalComponent } from '../user-detail-modal/user-detail-modal.component';
import { CreateUserModalComponent } from '../create-user-modal/create-user-modal.component';
import { EditUserModalComponent } from '../edit-user-modal/edit-user-modal.component';
// import { CreateOrganizationModalComponent } from '../create-organization-modal/create-organization-modal.component';
import { UserService, UserDetails, UserFilters, LicenseType, RankType } from '../../services/user.service';
import { formatMemberExpiry, isLifetimeMemberType } from '../../utils/membership.util';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    NgbPaginationModule, 
    NgbTooltipModule, 
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent, 
    StaffNavComponent
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss'
})
export class UserManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  isDesktopSidebarCollapsed = false;
  private userSubscription: Subscription | undefined;
  
  users: UserDetails[] = [];
  filteredUsers: UserDetails[] = [];
  licenses: LicenseType[] = [];
  ranks: RankType[] = [];
  isLoading = false;
  isLoadingLicenses = false;
  isLoadingRanks = false;
  error: string | null = null;
  showMockDataWarning = false;

  // Tab management
  activeTab: 'all' | 'approved' | 'pending' | 'rejected' = 'all';
  tabs = [
    { id: 'all' as const, label: 'ทั้งหมด', userStatusId: undefined, badgeClass: 'bg-secondary' },
    { id: 'approved' as const, label: 'Approved', userStatusId: 1, badgeClass: 'bg-success' },
    { id: 'pending' as const, label: 'รอ Approve', userStatusId: 2, badgeClass: 'bg-warning' },
    { id: 'rejected' as const, label: 'ไม่ผ่านการ Approve', userStatusId: 3, badgeClass: 'bg-danger' }
  ];
  
  // Tab counts - store counts for each tab separately
  tabCounts: { [key: string]: number } = {
    all: 0,
    approved: 0,
    pending: 0,
    rejected: 0
  };

  // Filter properties
  filters: UserFilters = {
    searchTerm: '',
    cidSearch: '',
    organization: '',
    vatMemberType: '',
    rankLevel: '',
    vatExpiring: false,
    valuerExpiring: false,
    status: 'all'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;
  
  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal
  ) {}

  /**
   * Parse number from string or number value
   */
  parseNumber(value: number | string | undefined): number {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  ngOnInit(): void {
    console.log('🔄 UserManagementComponent ngOnInit เริ่มต้น');
    console.log('🔍 Navigation type detection:');
    console.log('  - window.performance.navigation.type:', window.performance?.navigation?.type);
    console.log('  - document.readyState:', document.readyState);
    console.log('  - Current timestamp:', Date.now());
    
    // Subscribe to auth user first
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      // Force change detection when user changes
      this.cdr.detectChanges();
    });

    // Use NgZone to ensure operations run inside Angular zone
    this.ngZone.run(() => {
      // Delay to ensure component is fully initialized
      setTimeout(() => {
        console.log('⏰ เริ่มโหลดข้อมูลหลังจาก timeout (inside NgZone)');
        this.loadAllTabCounts(); // Load counts for all tabs first
        this.loadUsers();
        this.loadLicenses();
        this.loadRanks();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ UserManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onDesktopSidebarToggle(isCollapsed: boolean): void {
    this.isDesktopSidebarCollapsed = isCollapsed;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
    
    switch (route) {
      case 'users':
        // Already on user management page
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all ranks from the server
   */
  loadRanks(): void {
    console.log('🚀 เริ่มโหลดข้อมูล ranks...');
    
    this.isLoadingRanks = true;
    
    const sub = this.userService.getAllRanks().subscribe({
      next: (ranks) => {
        this.ngZone.run(() => {
          console.log('✅ โหลดข้อมูล ranks สำเร็จ:', ranks.length, 'รายการ');
          console.log('📊 ข้อมูล ranks ที่ได้รับ:', ranks);
          
          if (ranks && Array.isArray(ranks)) {
            this.ranks = [...ranks];
          } else {
            console.warn('⚠️ ข้อมูล ranks ที่ได้รับไม่ถูกต้อง:', ranks);
            this.ranks = [];
          }
          
          this.isLoadingRanks = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ เกิดข้อผิดพลาดในการโหลด ranks:', error);
          this.ranks = [];
          this.isLoadingRanks = false;
          this.cdr.detectChanges();
        });
      }
    });
    
    this.subscription.add(sub);
  }

  /**
   * Load all licenses from the server
   */
  loadLicenses(): void {
    console.log('🚀 เริ่มโหลดข้อมูล licenses...');
    
    this.isLoadingLicenses = true;
    
    const sub = this.userService.getAllLicenses().subscribe({
      next: (licenses) => {
        this.ngZone.run(() => {
          console.log('✅ โหลดข้อมูล licenses สำเร็จ:', licenses.length, 'รายการ');
          console.log('📊 ข้อมูล licenses ที่ได้รับ:', licenses);
          
          if (licenses && Array.isArray(licenses)) {
            this.licenses = [...licenses];
          } else {
            console.warn('⚠️ ข้อมูล licenses ที่ได้รับไม่ถูกต้อง:', licenses);
            this.licenses = [];
          }
          
          this.isLoadingLicenses = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ เกิดข้อผิดพลาดในการโหลด licenses:', error);
          this.licenses = [];
          this.isLoadingLicenses = false;
          this.cdr.detectChanges();
        });
      }
    });
    
    this.subscription.add(sub);
  }

  /**
   * Refresh all data (users, licenses, and ranks)
   */
  refreshData(): void {
    console.log('🔄 รีเฟรชข้อมูลทั้งหมด...');
    this.loadAllTabCounts(); // Refresh tab counts
    this.loadUsers();
    this.loadLicenses();
    this.loadRanks();
  }

  /**
   * Load counts for all tabs
   */
  loadAllTabCounts(): void {
    console.log('🔢 Loading counts for all tabs...');
    
    // Load count for each tab
    this.tabs.forEach(tab => {
      const sub = this.userService.getAllUsersWithDetails(tab.userStatusId).subscribe({
        next: (users) => {
          this.ngZone.run(() => {
            this.tabCounts[tab.id] = users.length;
            console.log(`✅ Tab "${tab.label}" count:`, users.length);
            this.cdr.detectChanges();
          });
        },
        error: (error) => {
          console.error(`❌ Error loading count for tab "${tab.label}":`, error);
          this.tabCounts[tab.id] = 0;
        }
      });
      this.subscription.add(sub);
    });
  }

  /**
   * Switch between tabs and reload data
   */
  switchTab(tabId: 'all' | 'approved' | 'pending' | 'rejected'): void {
    console.log('🔄 Switching to tab:', tabId);
    this.activeTab = tabId;
    this.page = 1; // Reset to first page
    this.loadUsers(); // Reload data with new filter
  }

  /**
   * Get count for specific tab
   */
  getTabCount(tabId: string): number {
    // Return the stored count for this tab
    return this.tabCounts[tabId] || 0;
  }

  /**
   * Load all users from the server
   */
  loadUsers(): void {
    console.log('🚀 เริ่มโหลดข้อมูลผู้ใช้...');
    console.log('🔍 Component state - users:', this.users.length, 'filteredUsers:', this.filteredUsers.length);
    console.log('🔄 กำลังตั้งค่า isLoading = true');
    console.log('📑 Active tab:', this.activeTab);
    
    this.isLoading = true;
    this.error = null;
    this.showMockDataWarning = false;
    
    // Force initial change detection
    this.cdr.detectChanges();

    // Get userStatusId based on active tab
    const currentTab = this.tabs.find(tab => tab.id === this.activeTab);
    const userStatusId = currentTab?.userStatusId;

    const sub = this.userService.getAllUsersWithDetails(userStatusId).subscribe({
      next: (users) => {
        // Run inside NgZone to ensure Angular detects changes
        this.ngZone.run(() => {
          console.log('✅ โหลดข้อมูลสำเร็จ:', users.length, 'รายการ');
          console.log('📊 ข้อมูลที่ได้รับ:', users);
          
          // Make sure we have valid data
          if (users && Array.isArray(users)) {
            // Sort by userInformation.updatedAt in descending order (latest first)
            const sortedUsers = [...users].sort((a, b) => {
              const dateA = new Date(a.userInformation?.updatedAt || a.updatedAt || 0).getTime();
              const dateB = new Date(b.userInformation?.updatedAt || b.updatedAt || 0).getTime();
              return dateB - dateA; // DESC order (latest first)
            });
            
            this.users = sortedUsers;
            this.showMockDataWarning = users.length > 0 && users[0].id.startsWith('mock-');
            
            console.log('🔄 กำลังเรียก applyFilters...');
            this.applyFilters();
            console.log('📋 หลังจาก applyFilters - filteredUsers:', this.filteredUsers.length);
          } else {
            console.warn('⚠️ ข้อมูลที่ได้รับไม่ถูกต้อง:', users);
            this.users = [];
            this.filteredUsers = [];
            this.collectionSize = 0;
          }
          
          console.log('🔄 กำลังตั้งค่า isLoading = false (next)');
          this.isLoading = false;
          
          // Force change detection multiple times
          this.cdr.detectChanges();
          
          console.log('✅ isLoading หลังจากตั้งค่า:', this.isLoading);
          console.log('📊 Final state - users:', this.users.length, 'filtered:', this.filteredUsers.length, 'collection:', this.collectionSize);
          
          // Additional change detection after a micro-task
          setTimeout(() => {
            console.log('🔍 ตรวจสอบข้อมูลหลัง setTimeout - users:', this.users.length, 'filtered:', this.filteredUsers.length);
            this.cdr.detectChanges();
          }, 0);
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ เกิดข้อผิดพลาด:', error);
          console.error('❌ Error details:', error.message, error.stack);
          
          this.error = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้งาน: ' + (error.message || 'Unknown error');
          this.users = [];
          this.filteredUsers = [];
          this.collectionSize = 0;
          
          console.log('🔄 กำลังตั้งค่า isLoading = false (error)');
          this.isLoading = false;
          console.log('✅ isLoading หลังจากตั้งค่า:', this.isLoading);
          
          // Force change detection on error
          this.cdr.detectChanges();
        });
      },
      complete: () => {
        console.log('🏁 Observable completed');
      }
    });
    
    console.log('🔍 isLoading ปัจจุบัน:', this.isLoading);
    this.subscription.add(sub);
  }

  /**
   * Apply filters to user list
   */
  applyFilters(): void {
    console.log('🔍 เริ่ม applyFilters - users:', this.users.length, 'filters:', this.filters);
    
    if (!this.users || this.users.length === 0) {
      console.log('⚠️ ไม่มีข้อมูลผู้ใช้สำหรับการกรอง');
      this.filteredUsers = [];
      this.collectionSize = 0;
      this.page = 1;
      
      // Force change detection when no data
      this.cdr.detectChanges();
      return;
    }

    try {
      this.filteredUsers = this.userService.filterUsers(this.users, this.filters);
      this.collectionSize = this.filteredUsers.length;
      this.page = 1; // Reset to first page when filters change
      
      console.log('✅ applyFilters สำเร็จ - filteredUsers:', this.filteredUsers.length, 'collectionSize:', this.collectionSize);
      
      // Force change detection after filtering
      this.cdr.detectChanges();
      
      // Debug the current page users
      const currentPageUsers = this.getCurrentPageUsers();
      console.log('📄 Current page users:', currentPageUsers.length);
      
    } catch (error) {
      console.error('❌ เกิดข้อผิดพลาดใน applyFilters:', error);
      this.filteredUsers = [...this.users]; // Create new array reference
      this.collectionSize = this.users.length;
      this.page = 1;
      
      // Force change detection on error
      this.cdr.detectChanges();
    }
  }

  /**
   * Get current page users
   */
  getCurrentPageUsers(): UserDetails[] {
    console.log('📖 getCurrentPageUsers called - page:', this.page, 'pageSize:', this.pageSize, 'filteredUsers:', this.filteredUsers.length);
    
    if (!this.filteredUsers || this.filteredUsers.length === 0) {
      console.log('⚠️ No filtered users available');
      return [];
    }
    
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const currentPageUsers = this.filteredUsers.slice(startIndex, endIndex);
    
    console.log('📄 Page calculation - startIndex:', startIndex, 'endIndex:', endIndex, 'result:', currentPageUsers.length, 'users');
    
    return currentPageUsers;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      cidSearch: '',
      organization: '',
      vatMemberType: '',
      rankLevel: '',
      vatExpiring: false,
      valuerExpiring: false,
      status: 'all'
    };
    this.applyFilters();
  }

  /**
   * Sort users by field
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredUsers.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'name':
          aValue = `${a.userInformation?.firstName || ''} ${a.userInformation?.lastName || ''}`;
          bValue = `${b.userInformation?.firstName || ''} ${b.userInformation?.lastName || ''}`;
          break;
        case 'cid':
          aValue = a.cid || '';
          bValue = b.cid || '';
          break;
        case 'organization':
          aValue = a.userInformation?.company?.organizationName || '';
          bValue = b.userInformation?.company?.organizationName || '';
          break;
        case 'vatId':
          aValue = a.userInformation?.vatId || '';
          bValue = b.userInformation?.vatId || '';
          break;
        case 'vatExpired':
          aValue = new Date(a.userInformation?.vatExpired || '');
          bValue = new Date(b.userInformation?.vatExpired || '');
          break;
        case 'valuerId':
          aValue = a.userInformation?.valuerId || '';
          bValue = b.userInformation?.valuerId || '';
          break;
        case 'valuerExpired':
          aValue = new Date(a.userInformation?.valuerExpired || '');
          bValue = new Date(b.userInformation?.valuerExpired || '');
          break;
        case 'totalCpd':
          aValue = this.parseNumber(a.userInformation?.totalCpd);
          bValue = this.parseNumber(b.userInformation?.totalCpd);
          break;
        case 'verificationStatus':
          aValue = a.userInformation?.userStatus?.name || '';
          bValue = b.userInformation?.userStatus?.name || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.collectionSize = this.filteredUsers.length;
  }

  /**
   * Toggle user status
   */
  toggleUserStatus(user: UserDetails): void {
    const newStatus = !user.isActive;
    const confirmation = confirm(
      `คุณต้องการ${newStatus ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}ผู้ใช้ ${user.userInformation?.firstName} ${user.userInformation?.lastName} หรือไม่?`
    );

    if (confirmation) {
      const sub = this.userService.updateUserStatus(user.id, newStatus).subscribe({
        next: () => {
          user.isActive = newStatus;
          // Optional: Show success message
        },
        error: (error) => {
          console.error('Error updating user status:', error);
          alert('เกิดข้อผิดพลาดในการเปลี่ยนสถานะผู้ใช้งาน');
        }
      });

      this.subscription.add(sub);
    }
  }

  /**
   * Open user detail modal
   */
  openUserDetailModal(user: UserDetails): void {
    console.log('🔍 Opening user detail modal for:', user.id, user);
    
    const modalRef = this.modalService.open(UserDetailModalComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      scrollable: true
    });

    console.log('🔧 Setting userId on modal component:', user.id);
    modalRef.componentInstance.userId = user.id;
    console.log('✅ Modal component instance:', modalRef.componentInstance);

    modalRef.result.then(
      (result) => {
        console.log('✅ User detail modal closed with result:', result);
        
        // Refresh data if user was approved or rejected
        if (result?.approved || result?.rejected) {
          console.log('🔄 User status changed, refreshing data...');
          this.refreshData();
        }
      },
      (dismissed) => {
        console.log('❌ User detail modal dismissed:', dismissed);
      }
    );
  }

  /**
   * Edit user information
   */
  editUser(user: UserDetails): void {
    console.log('🔧 Edit user:', user);
    
    try {
      const modalRef = this.modalService.open(EditUserModalComponent, {
        size: 'xl',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'edit-user-modal-wide',
        scrollable: true,
        centered: true
      });
      
      // Pass user data to modal
      modalRef.componentInstance.userData = user;
      
      modalRef.result.then(
        (result: any) => {
          console.log('Edit user modal closed with result:', result);
          
          if (result && result.success) {
            console.log('User updated successfully:', result.userData);
            
            // Show success message
            alert(`✅ แก้ไขข้อมูลผู้ใช้งาน "${result.userData.firstName} ${result.userData.lastName}" เรียบร้อยแล้ว`);
            
            // Refresh user list to show updated data
            this.refreshData();
          }
        },
        (dismissed: any) => {
          console.log('Edit user modal dismissed:', dismissed);
        }
      ).catch((error: any) => {
        console.error('Error with edit user modal:', error);
      });
      
    } catch (error: any) {
      console.error('Error opening edit user modal:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างแก้ไขผู้ใช้งาน';
      
      setTimeout(() => {
        this.error = null;
        this.cdr.detectChanges();
      }, 5000);
    }
  }

  /**
   * Delete user with confirmation
   */
  deleteUser(user: UserDetails): void {
    console.log('🗑️ Delete user:', user);
    
    const confirmation = confirm(
      `⚠️ คุณต้องการลบผู้ใช้ "${user.userInformation?.firstName} ${user.userInformation?.lastName}" หรือไม่?\n\n` +
      `การดำเนินการนี้ไม่สามารถยกเลิกได้!`
    );

    if (!confirmation) {
      return;
    }

    // Double confirmation for safety
    const doubleConfirmation = confirm(
      `❗ ยืนยันการลบผู้ใช้อีกครั้ง?\n\n` +
      `ผู้ใช้: ${user.userInformation?.firstName} ${user.userInformation?.lastName}\n` +
      `อีเมล: ${user.email}\n` +
      `ID Card: ${user.cid}\n\n` +
      `พิมพ์ "DELETE" เพื่อยืนยัน`
    );

    if (doubleConfirmation) {
      // TODO: Implement actual delete functionality with API call
      // For now, just remove from local array
      const index = this.users.findIndex(u => u.id === user.id);
      if (index > -1) {
        this.users.splice(index, 1);
        this.applyFilters(); // Refresh the filtered list
        alert(`✅ ลบผู้ใช้ "${user.userInformation?.firstName} ${user.userInformation?.lastName}" เรียบร้อยแล้ว`);
      }
      
      // When API is ready, use this:
      /*
      const sub = this.userService.deleteUser(user.id).subscribe({
        next: () => {
          const index = this.users.findIndex(u => u.id === user.id);
          if (index > -1) {
            this.users.splice(index, 1);
            this.applyFilters();
          }
          alert(`✅ ลบผู้ใช้ "${user.userInformation?.firstName} ${user.userInformation?.lastName}" เรียบร้อยแล้ว`);
        },
        error: (error) => {
          console.error('Error deleting user:', error);
          alert('❌ เกิดข้อผิดพลาดในการลบผู้ใช้งาน');
        }
      });
      this.subscription.add(sub);
      */
    }
  }

  /**
   * Export users to CSV
   */
  exportToCSV(): void {
    this.userService.exportToCSV(this.filteredUsers);
  }

  /**
   * Get status class for styling
   */
  getStatusClass(user: UserDetails): string {
    return user.isActive ? 'status-active' : 'status-inactive';
  }

  /**
   * Get expiration status for VAT
   */
  getVATExpirationStatus(user: UserDetails): string {
    // สมาชิกตลอดชีพไม่มีวันหมดอายุ ถือว่าใช้งานได้เสมอ
    if (isLifetimeMemberType(user.userInformation?.vatType)) return 'valid';

    if (!user.userInformation?.vatExpired) return '';
    
    const expiryDate = new Date(user.userInformation?.vatExpired);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysFromNow = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));
    
    if (expiryDate < today) return 'expired';
    if (expiryDate < thirtyDaysFromNow) return 'expiring-soon';
    if (expiryDate < ninetyDaysFromNow) return 'expiring-warning';
    return 'valid';
  }

  /**
   * Get expiration status for Valuer
   */
  getValuerExpirationStatus(user: UserDetails): string {
    if (!user.userInformation?.valuerExpired) return '';
    
    const expiryDate = new Date(user.userInformation?.valuerExpired);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysFromNow = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000));
    
    if (expiryDate < today) return 'expired';
    if (expiryDate < thirtyDaysFromNow) return 'expiring-soon';
    if (expiryDate < ninetyDaysFromNow) return 'expiring-warning';
    return 'valid';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }

  /**
   * ข้อความวันหมดอายุบัตรสมาชิก (ตลอดชีพจะแสดงคำว่า "ตลอดชีพ")
   */
  getVATExpiryText(user: UserDetails): string {
    return formatMemberExpiry(
      user.userInformation?.vatType,
      user.userInformation?.vatExpired,
      date => this.formatDate(date)
    );
  }

  /**
   * Get page numbers for pagination (using NgBootstrap pagination)
   */
  getPageNumbers(): number[] {
    // This method is no longer needed with NgBootstrap pagination
    return [];
  }

  /**
   * Get verification status class for badge styling
   */
  getVerificationStatusClass(user: UserDetails): string {
    const statusName = user.userInformation?.userStatus?.name;
    
    if (!statusName) {
      return 'bg-secondary';
    }
    
    const statusLower = statusName.toLowerCase();
    
    // Orange for pending approval
    if (statusLower.includes('รอ approve') || statusLower.includes('รออนุมัติ') || statusLower.includes('pending')) {
      return 'bg-warning';
    }
    
    // Red for rejected
    if (statusLower.includes('ไม่ผ่าน') || statusLower.includes('ปฏิเสธ') || statusLower.includes('reject')) {
      return 'bg-danger';
    }
    
    // Green for approved
    if (statusLower.includes('approved') || statusLower.includes('อนุมัติ') || statusLower.includes('ผ่าน')) {
      return 'bg-success';
    }
    
    // Default gray
    return 'bg-secondary';
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByUserId(index: number, user: UserDetails): string {
    return user.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state for proper z-index management
   */
  onDropdownOpenChange(isOpen: boolean, userId: string): void {
    // Find the table row element and manage z-index
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${userId}`);
        const tableRow = dropdownButton?.closest('tr');
        const table = dropdownButton?.closest('table');
        const tableContainer = dropdownButton?.closest('.table-responsive');
        
        if (tableRow) {
          if (isOpen) {
            // Add dropdown-active class for styling
            tableRow.classList.add('dropdown-active');
            
            // Set z-index for the row and its parent containers
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';
            
            // Ensure table container doesn't clip the dropdown
            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }
            
            // Check if dropdown is near bottom of viewport
            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            
            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              // Position dropdown above the button if near bottom
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            // Clean up when dropdown closes
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';
            
            // Reset table container overflow on desktop
            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }
            
            // Reset dropdown position
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }

  /**
   * Create new user
   */
  createNewUser(): void {
    try {
      console.log('Opening create user modal');
      
      const modalRef = this.modalService.open(CreateUserModalComponent, {
        size: 'xl',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'create-user-modal-wide',
        scrollable: true,
        centered: true
      });
      
      modalRef.result.then(
        (result: any) => {
          console.log('Create user modal closed with result:', result);
          
          if (result && result.success) {
            console.log('User created successfully:', result.userData);
            
            // Show success message
            alert(`✅ สร้างผู้ใช้งาน "${result.userData.firstName} ${result.userData.lastName}" เรียบร้อยแล้ว`);
            
            // Refresh user list to show new user (in real implementation)
            this.refreshData();
          }
        },
        (dismissed: any) => {
          console.log('Create user modal dismissed:', dismissed);
        }
      ).catch((error: any) => {
        console.error('Error with create user modal:', error);
      });
      
    } catch (error: any) {
      console.error('Error opening create user modal:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างสร้างผู้ใช้งาน';
      
      setTimeout(() => {
        this.error = null;
        this.cdr.detectChanges();
      }, 5000);
    }
  }

  /**
   * Create new organization
   */
  createNewOrganization(): void {
    // TODO: Implement organization modal when ready
    alert('ฟีเจอร์เพิ่มหน่วยงานกำลังพัฒนา');
  }

  /**
   * View user details in modal (read-only mode)
   */
  viewUserDetails(user: UserDetails): void {
    try {
      console.log('Opening user detail modal for user:', user.id);
      
      const modalRef = this.modalService.open(EditUserModalComponent, {
        size: 'xl',
        backdrop: 'static',
        keyboard: false,
        windowClass: 'edit-user-modal-wide',
        scrollable: true,
        centered: true
      });
      
      // Pass user data to modal
      modalRef.componentInstance.userData = user;
      
      modalRef.result.then(
        (result: any) => {
          console.log('User detail modal closed with result:', result);
        },
        (dismissed: any) => {
          console.log('User detail modal dismissed:', dismissed);
        }
      ).catch((error: any) => {
        console.error('Error with user detail modal:', error);
      });
      
    } catch (error: any) {
      console.error('Error opening user detail modal:', error);
      this.error = 'เกิดข้อผิดพลาดในการเปิดหน้าต่างรายละเอียดผู้ใช้';
      
      setTimeout(() => {
        this.error = null;
        this.cdr.detectChanges();
      }, 5000);
    }
  }
} 